
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AssessmentQuestion } from './AssessmentQuestion';

interface IndustryImpactSectionProps {
  onComplete: (data: { score: number; responses: Record<string, number> }) => void;
  initialData?: { score: number; responses: Record<string, number> };
}

export const IndustryImpactSection = ({ onComplete, initialData }: IndustryImpactSectionProps) => {
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>(
    initialData?.responses || {}
  );

  const questions = [
    {
      id: 'thoughtLeadership',
      title: 'Thought Leadership',
      question: 'How do you influence the product community?',
      options: [
        { text: 'I participate actively in product discussions and stay engaged with industry trends. I\'m part of the product community conversation.', points: 2 },
        { text: 'I share valuable product insights that others find useful. My perspectives contribute meaningfully to product discussions within and beyond my company.', points: 4 },
        { text: 'I speak at industry events and am recognized for my expertise. People seek out my views on product topics.', points: 6 },
        { text: 'I shape industry direction through my thought leadership. My ideas influence how others think about product management beyond my organization.', points: 8 }
      ],
      evidence: [
        { 
          title: 'Thought Leadership',
          description: 'Share an example of your thought leadership in product'
        },
        {
          title: 'Community Impact',
          description: 'How has your work influenced the broader product community?'
        }
      ]
    },
    {
      id: 'professionalImpact',
      title: 'Professional Impact',
      question: 'How do you contribute to the profession?',
      options: [
        { text: 'I uphold the standards of good product management in my work. I represent the profession well through my practices.', points: 1 },
        { text: 'I advise other product professionals and help them navigate challenges. People seek my guidance on difficult product situations.', points: 3 },
        { text: 'I influence product practices beyond my immediate organization. My approaches are adopted by other product teams or companies.', points: 5 },
        { text: 'I advance the profession through significant contributions. I help define what great product leadership looks like in today\'s environment.', points: 7 }
      ],
      evidence: [
        {
          title: 'Professional Contribution',
          description: 'Describe your contribution to the product profession'
        },
        {
          title: 'Practice Adoption',
          description: 'How have others adopted your product practices?'
        }
      ]
    }
  ];

  const handleSelect = (points: number) => {
    const currentQuestionId = questions[currentQuestion].id;
    setResponses(prev => ({
      ...prev,
      [currentQuestionId]: points
    }));
  };

  const calculateScore = (responses: Record<string, number>): number => {
    return Object.values(responses).reduce((sum, points) => sum + points, 0);
  };

  const handleNext = () => {
    if (currentQuestion === questions.length - 1) {
      const finalData = {
        score: calculateScore(responses),
        responses
      };
      
      toast({
        title: "Section completed",
        description: "Your Industry Impact & Influence assessment has been saved.",
      });
      
      onComplete(finalData);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestion(prev => Math.max(0, prev - 1));
  };

  const currentQuestionData = questions[currentQuestion];
  const selectedPoints = responses[currentQuestionData.id];

  return (
    <Card className="max-w-3xl mx-auto">
      <AssessmentQuestion
        title={currentQuestionData.title}
        question={currentQuestionData.question}
        options={currentQuestionData.options}
        evidence={currentQuestionData.evidence}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSelect={handleSelect}
        selectedPoints={selectedPoints}
        showPrevious={currentQuestion > 0}
      />
    </Card>
  );
};

export default IndustryImpactSection;
